/**
 * 見つかる物（§1 裏テーマ / §10.18）。
 *
 * 概要の3番目 ——「猫が壊した物・見つけた物から廃墟の背景が解けていく」——
 * を実際に動かす層。**きっかけは全部、猫が勝手にやったこと**で、
 * プレイヤーが探しに行ける物はひとつも無い（§2.5）。
 *
 * きっかけは4つ。
 *   dwell … 猫がその場所で長くくつろいだ
 *   break … その階のその什器を猫が壊した
 *   swat  … その階のその飾りを猫が叩き落とした
 *   floor … その階へ猫が初めて届いた
 *
 * break と swat は**壊れた場所に出る**ので、原因と結果が同じ場所に並ぶ（§2.2）。
 */
import type { GameState, MemoryInstance } from './state';
import { BUILDING } from './state';

/** その行がどの階か。什器や飾りの座標から階を引くのに使う */
export function floorIdAt(row: number): string | null {
  let best: { id: string; d: number } | null = null;
  for (const floor of BUILDING.floors) {
    const d = floor.standRow - row;
    if (d < 0 || d > 5) continue;
    if (!best || d < best.d) best = { id: floor.id, d };
  }
  return best?.id ?? null;
}

export type Trigger =
  | { on: 'dwell' }
  | { on: 'break'; salvage: string; floor: string }
  | { on: 'swat'; fixture: string; floor: string }
  | { on: 'floor'; floor: string };

/** 猫が何かした、という報せ */
export type Happening =
  | { on: 'break'; salvage: string; c: number; r: number }
  | { on: 'swat'; fixture: string; c: number; r: number }
  | { on: 'floor'; floor: string };

function matches(trigger: Trigger, what: Happening): boolean {
  if (trigger.on !== what.on) return false;
  if (trigger.on === 'break' && what.on === 'break') {
    return trigger.salvage === what.salvage && trigger.floor === floorIdAt(what.r);
  }
  if (trigger.on === 'swat' && what.on === 'swat') {
    return trigger.fixture === what.fixture && trigger.floor === floorIdAt(what.r);
  }
  if (trigger.on === 'floor' && what.on === 'floor') {
    return trigger.floor === what.floor;
  }
  return false;
}

/**
 * 起きたことに反応して、まだ見つかっていない物を1つだけ出す。
 * 「1つだけ」にしてあるのは、同じ種類の什器が同じ階に2つある時に
 * まとめて全部出てしまわないようにするため（2つ目を壊しても、次の物が出る）。
 */
export function fireDiscovery(state: GameState, what: Happening): MemoryInstance | null {
  for (const m of state.memories) {
    if (m.found) continue;
    if (!matches(m.trigger as Trigger, what)) continue;
    m.found = true;
    // 場所を持たない物は、起きた場所に出る
    if (what.on !== 'floor' && m.trigger.on !== 'floor') {
      m.c = what.c;
      m.r = what.r;
    }
    return m;
  }
  return null;
}
